import React from 'react';
import { animated, useSpring } from 'react-spring';

import { SPRINGS } from '@/constants';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import VisuallyHidden from '@/components/VisuallyHidden';

import useBoopMinimal from '@/hooks/use-boop-minimal';
import Svg from './Svg';

interface Props {
  size?: number;
  isHovering: boolean;
  isRunning: boolean;
  timing?: number;
  style?: React.CSSProperties;
  hideMask?: boolean;
}

const SkipBackIcon = ({
  size = 16,
  isHovering,
  isRunning,
  timing = 150,
  style = {},
  hideMask = false,
}: Props) => {
  const id = React.useId();

  const isBooped = useBoopMinimal(isHovering, { timing });

  const maskId = `skip-icon-mask-${id}`;

  const prefersReducedMotion = usePrefersReducedMotion();

  const springSettings = {
    config: isRunning
      ? { tension: 100, friction: 40 }
      : SPRINGS.springy,
    immediate: prefersReducedMotion,
  };

  const points = [19, 20, 9, 12, 19, 4, 19, 20];
  const boopDistance = 3;
  const holdDistance = 5;
  const distance = isRunning ? holdDistance : boopDistance;

  const polygonSpring = useSpring({
    // In the toolbar, this icon is sometimes wrapped in a <Tooltip>. When that tooltip goes away, the component is freshly mounted, which means the animation fails (since the very first render IS the "contracted" position). We need this "from" to handle this edge case.
    from: {
      points: points.join(' '),
    },
    points:
      // The triangle moves between two positions. The default position is specified by the polygon points held in the `points` array. The 1st, 3rd, 5th… are horizontal values, the 2nd, 4th, 6th… are vertical.
      // We want to translate all of the horizontal points when the button is being interacted with. That's what this `map` does.
      // Remember, arrays are 0-indexed, and so the 1st item is actually the 0th item, and is even.
      isBooped || isRunning
        ? points
            .map((p, i) => (i % 2 === 0 ? p - distance : p))
            .join(' ')
        : points.join(' '),
    ...springSettings,
  });

  return (
    <>
      <Svg
        viewBox="0 0 24 24"
        fill="none"
        style={{
          width: size,
          height: size,
          opacity: isHovering ? 1 : 0.7,
          ...style,
        }}
      >
        {!hideMask && (
          <mask id={maskId}>
            <rect x="0" y="0" width="24" height="24" fill="black" />
            <rect x="9" y="0" width="16" height="24" fill="white" />
          </mask>
        )}
        <animated.line x1="5" y1="19" x2="5" y2="5" />
        <animated.polygon
          {...polygonSpring}
          mask={`url(#${maskId})`}
        />
      </Svg>
      <VisuallyHidden>Reset Code</VisuallyHidden>
    </>
  );
};

export default React.memo(SkipBackIcon);
